import { ClinicUserGroupForm } from "../../../components/clinic-user-groups/ClinicUserGroupForm";
import { useClinicUserGroup, ClinicUserGroupDetails } from "@/hooks/clinic-user-groups/useClinicUserGroup";
import { useNavigate, useParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import LoaderComponent from "@/components/common/LoaderComponent";
import { Card, CardContent } from "@/components/ui/card";
import { usersGraphqlService } from "@/services/api/users/usersGraphqlService";

const EditClinicUserGroup = () => {
  const { id } = useParams<{ id: string }>();
  const { editClinicUserGroup, getClinicUserGroupDetails, loading } = useClinicUserGroup();
  const { user, token, hasuraToken } = useAuth();
  const navigate = useNavigate();
  const initialUserIdsRef = useRef<number[]>([]);
  const initialClinicIdsRef = useRef<number[]>([]);

  const [groupDetails, setGroupDetails] = useState<ClinicUserGroupDetails | null>(null);
  const [fetchingDetails, setFetchingDetails] = useState(true);

  useEffect(() => {
    const fetchDetails = async () => {
      if (!id || !user?.id || !token) return;
      setFetchingDetails(true);
      const details = await getClinicUserGroupDetails(Number(id));
      if (details && details.users.length > 0 && hasuraToken) {
        // Fetch user types from GraphQL
        try {
          const userIds = details.users.map(u => u.userId);
          const result = await usersGraphqlService.getUsersByIds(hasuraToken, userIds);
          const userTypeMap = new Map<number, string>();
          result?.data?.pep_vw_user_dashboard?.forEach(u => {
            userTypeMap.set(u.id, u.user_type);
          });
          // Merge user_type into details.users
          details.users = details.users.map(u => ({
            ...u,
            userType: userTypeMap.get(u.userId) || '',
            appUserName: result?.data?.pep_vw_user_dashboard?.find(d => d.id === u.userId)?.user_name || u.appUserName,
          }));
        } catch (e) {
          console.warn('[EditClinicUserGroup] Failed to fetch user types:', e);
        }
      }
      if (details) {
        setGroupDetails(details);
        initialUserIdsRef.current = details.users.map(u => u.userId);
        initialClinicIdsRef.current = details.clinics.map(c => c.clinicId);
      }
      setFetchingDetails(false);
    };
    fetchDetails();
  }, [id, user?.id, token, hasuraToken]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (data: any) => {
    if (!id) return;

    try {
      const currentUserIds: number[] = data.assignedUsers.map((u: any) => u.id);
      const initialUserIds = initialUserIdsRef.current;

      const userIdsToAdd = currentUserIds.filter(uid => !initialUserIds.includes(uid));
      const userIdsToRemove = initialUserIds.filter(uid => !currentUserIds.includes(uid));

      const currentClinicIds: number[] = data.clinicIds || [];
      const initialClinicIds = initialClinicIdsRef.current;

      const clinicIdsToAdd = currentClinicIds.filter(cid => !initialClinicIds.includes(cid));
      const clinicIdsToRemove = initialClinicIds.filter(cid => !currentClinicIds.includes(cid));

      await editClinicUserGroup(Number(id), {
        clinicUserGroupId: Number(id),
        groupName: data.groupName,
        addUserIds: userIdsToAdd,
        removeUserIds: userIdsToRemove,
        addClinicIds: clinicIdsToAdd,
        removeClinicIds: clinicIdsToRemove,
      });
      navigate("/admin/clinic-user-groups");
    } catch (error) {
      // Error handled in hook
    }
  };

  if (fetchingDetails) {
    return (
      <div className="container max-w-4xl mx-auto p-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex justify-center py-8">
              <LoaderComponent />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const initialData = groupDetails ? {
    groupName: groupDetails.groupName,
    assignedUserIds: groupDetails.users.map(u => u.userId),
    clinicIds: groupDetails.clinics.map(c => c.clinicId),
    stateId: groupDetails.stateId,
  } : undefined;

  return (
    <ClinicUserGroupForm
      title="Edit Clinic User Group"
      onSubmit={handleSubmit}
      isSubmitting={loading}
      existingGroupId={id ? Number(id) : undefined}
      initialData={initialData}
      initialUsers={groupDetails?.users}
    />
  );
};

export default EditClinicUserGroup;
